import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import Text from "new_components/atoms/Text";
import Icon from "new_components/atoms/Icon";
import colors from "styles/colors";
import SearchBar from "new_components/molecules/SearchBar";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

const SubMenuContainer = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0px 8px 16px 0px;
  max-width: ${({ isPulse }) => (isPulse ? '100%' : "240px")};
`;

const SubMenuItem = styled.div`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  background-color: ${({ isActive }) =>
    isActive ? colors.primary[50] : "transparent"};
  border-radius: 4px;
  padding: 8px 16px;
  transition: background-color 0.2s, color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const GroupTitle = styled.div`
  padding-left: 8px;
  padding-top: 16px;
  padding-bottom: 4px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const HEADER_HEIGHT = 230;

const SubMenu = ({
  categories,
  contentRef,
  searchable,
  type = "multi",
  activeItemId,
  onMenuItemClick,
  wizardMode = false,
  isPulse,
  filters,
}) => {
  const [activeCategory, setActiveCategory] = useState(
    activeItemId || categories[0]?.id || categories[0]?.items[0]?.id
  );
  const [searchTerm, setSearchTerm] = useState("");

  const isGrouped = categories?.[0]?.groupTitle !== undefined;

  const getAllItems = () => {
    if (isGrouped) {
      return categories.flatMap(group => group.items);
    }
    return categories;
  };

  const allItems = getAllItems();

  const filteredCategories = isGrouped
    ? categories
        .map(group => {
          const filteredItems = group.items.filter(item =>
            item.categoryTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
            group.groupTitle.toLowerCase().includes(searchTerm.toLowerCase())
          );
          return filteredItems.length > 0 ? { ...group, items: filteredItems } : null;
        })
        .filter(Boolean)
    : categories.filter(item =>
        item.categoryTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );

  useEffect(() => {
    if (type === "multi") {
      const handleScroll = () => {
        const sections = allItems.map(({ id }) => document.getElementById(id));
        let active = null;
        let minDistance = Infinity;

        sections.forEach(section => {
          if (section) {
            const rect = section.getBoundingClientRect();
            const distanceFromTop = Math.abs(rect.top - HEADER_HEIGHT);

            if (distanceFromTop < minDistance) {
              minDistance = distanceFromTop;
              active = section.id;
            }
          }
        });

        if (active) setActiveCategory(active);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    } else {
      setActiveCategory(activeItemId);
    }
  }, [allItems, type, activeItemId]);

  const handleClick = (id, itemOnClick, disabled) => {
    if (disabled) return;

    if (itemOnClick) {
      itemOnClick();
    } else if (type === "multi") {
      const section = document.getElementById(id);
      if (section) {
        const yOffset = -HEADER_HEIGHT + 90;
        const y = section.offsetTop + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
        setActiveCategory(id);
      }
    } else if (type === "single") {
      setActiveCategory(id);
      onMenuItemClick?.(id);
    }
  };

  return (
    <SubMenuContainer isPulse={isPulse}>
      {searchable && (
        <SearchBar
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
          placeholder="Search"
        />
      )}
      {isGrouped
        ? filteredCategories.map(group => (
          <div key={group.groupTitle}>
              <GroupTitle>{group.groupTitle}</GroupTitle>
              {group.items
                .filter(item => item.id !== "DEFAULT")
                .map(item => (
                  <SubMenuItem
                    key={item.id}
                    isActive={activeCategory === item.id}
                    disabled={item.disabled}
                    onClick={() => handleClick(item.id, item.onClick, item.disabled)}
                  >
                    <MenuItemContent>
                      <Text
                        typographyStyle={{
                          fontSize: "14px",
                          fontWeight: activeCategory === item.id ? "600" : "400",
                          lineHeight: "140%",
                        }}
                        color={
                          activeCategory === item.id
                            ? colors.primary[500]
                            : colors.text.dark
                        }
                      >
                        {item.categoryTitle}
                      </Text>
                    </MenuItemContent>
                    {wizardMode && item?.isCompleted && (
                      <Icon
                        name={ICON_SET.checkMark}
                        size={16}
                        color={colors.secondary[500]}
                      />
                    )}
                  </SubMenuItem>
                ))}
            </div>
          ))
        : filteredCategories
            .filter(item => item.id !== "DEFAULT")
            .map(item => (
              <SubMenuItem
                key={item.id}
                isActive={activeCategory === item.id}
                disabled={item.disabled}
                onClick={() => handleClick(item.id, item.onClick, item.disabled)}
              >
                <MenuItemContent>
                  <Text
                    typographyStyle={{
                      fontSize: "14px",
                      fontWeight: activeCategory === item.id ? "600" : "400",
                      lineHeight: "140%",
                    }}
                    color={
                      activeCategory === item.id
                        ? colors.primary[500]
                        : colors.text.dark
                    }
                  >
                    {item.categoryTitle}
                  </Text>
                </MenuItemContent>
                {wizardMode && item?.isCompleted && (
                  <Icon
                    name={ICON_SET.checkMark}
                    size={16}
                    color={colors.secondary[500]}
                  />
                )}
              </SubMenuItem>
            ))}
    </SubMenuContainer>
  );
};

SubMenu.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        categoryTitle: PropTypes.string.isRequired,
        onClick: PropTypes.func,
        disabled: PropTypes.bool,
        isCompleted: PropTypes.bool,
      }),
      PropTypes.shape({
        groupTitle: PropTypes.string.isRequired,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            categoryTitle: PropTypes.string.isRequired,
            onClick: PropTypes.func,
            disabled: PropTypes.bool,
            isCompleted: PropTypes.bool,
          })
        ).isRequired,
      }),
    ])
  ).isRequired,
  contentRef: PropTypes.object.isRequired,
  searchable: PropTypes.bool,
  type: PropTypes.oneOf(["multi", "single"]),
  activeItemId: PropTypes.string,
  onMenuItemClick: PropTypes.func,
  wizardMode: PropTypes.bool,
};

export default SubMenu;